export type SectionIconEntry = {
    key: string;
    labelKey: string;
    path: string;
};

// Brand-color fallback used by every tab when `state.global.infoUser.maincolor`
// is empty. Kept here so a future brand-color change touches a single line.
export const SCAN_TO_DEFAULT_MAIN_COLOR = "#c8a97e" as const;

// Pixel size of the small inline icons (e.g. the ResetIcon next to every
// "Réinitialiser" control), repeated across all rubric tabs. Centralized so a
// global icon-size change (a11y zoom, design refresh) is a single edit.
export const ICON_SIZE_SMALL = 14 as const;

// Default locale used by the preview (banner title/description fallbacks
// and the inline preview language selector).
export const SCAN_TO_DEFAULT_LANGUAGE = "fr" as const;

// Default text color for the banner title / description overlay.
export const SCAN_TO_DEFAULT_TEXT_COLOR = "#ffffff" as const;

// Sentinel value for `selectedIcon` when the user uploaded a custom icon
// instead of picking one from SECTION_ICONS. Used by Actions, Offres,
// Publicites and MaCommande tabs.
export const CUSTOM_ICON_KEY = "custom" as const;

// Sentinel for the in-progress « add option » form in AppelServeur:
// distinguishes a user-uploaded custom icon (data URL preview) from the
// SECTION_ICONS keys. Differs from CUSTOM_ICON_KEY to avoid collision with
// persisted QuickOption icons.
export const CUSTOM_ICON_NEW_KEY = "__custom__" as const;

// Display-type sentinel exposed by the Mobile/Desktop tabs. The string
// values map to UI buttons; the title-cased keys (DISPLAY_KEY_*) are the
// actual `BannerImages`/`OfferImages` object keys used by the slice.
export const DISPLAY_TYPE_MOBILE = "mobile" as const;
export const DISPLAY_KEY_MOBILE = "Mobile" as const;
export const DISPLAY_KEY_DESKTOP = "Desktop" as const;

// `MyOrderConfig.previewMode` sentinels — toggle the order preview between
// « commande en cours » and « pas de commande active ».
export const PREVIEW_MODE_WITH_ORDER = "with_order" as const;
export const PREVIEW_MODE_NO_ORDER = "no_order" as const;

// Indices of specific tabs in ModalContentWrapper's `tabs` array.
// Keep in sync with the `tabs` order in ModalContentWrapper/index.tsx.
//   • ACTIVITY: the setup step (pick activity / app name) — it has no preview.
//   • BANNER..MISCELLANEOUS: each maps to a scrollable preview section, so
//     opening a tab scrolls the shared preview to its section.
//   • PAYMENT / SERVER_CALL: drive which popup overlay the preview shows.
export const ACTIVITY_TAB_INDEX = 0 as const;
export const BANNER_TAB_INDEX = 1 as const;
export const OFFERS_TAB_INDEX = 2 as const;
export const ACTIONS_TAB_INDEX = 3 as const;
export const MY_ORDER_TAB_INDEX = 5 as const;
export const ADVERTISING_TAB_INDEX = 4 as const;
export const MISCELLANEOUS_TAB_INDEX = 6 as const;
export const PAYMENT_TAB_INDEX = 7 as const;
export const SERVER_CALL_TAB_INDEX = 8 as const;

export const LANGUAGE_CODES: Array<{ code: string; labelKey: string }> = [
    { code: "fr", labelKey: "French" },
    { code: "en", labelKey: "English" },
    { code: "ru", labelKey: "Russian" },
    { code: "it", labelKey: "Italian" },
    { code: "es", labelKey: "Spanish" },
    { code: "pt", labelKey: "Portuguese" },
    { code: "de", labelKey: "German" },
    { code: "ar", labelKey: "Arabic" },
];

// Languages that render right-to-left. Arabic is the only RTL locale among
// LANGUAGE_CODES today; add future RTL locales (e.g. "he") here.
export const RTL_LANGUAGES: string[] = ["ar"];

// True when the preview should render right-to-left for the given language.
export function isRtlLanguage(lang: string | undefined | null): boolean {
    return lang != null && RTL_LANGUAGES.includes(lang);
}

// Promotional / highlight icons shared by Actions, Offres, Publicites.
// MaCommande uses its own order-context icon set.
export const SECTION_ICONS: SectionIconEntry[] = [
    {
        key: "tag",
        labelKey: "Tag",
        path:
            "M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z",
    },
    {
        key: "star",
        labelKey: "Star",
        path:
            "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
    },
    {
        key: "gift",
        labelKey: "Gift",
        path:
            "M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z",
    },
    {
        key: "percent",
        labelKey: "Percent",
        path:
            "M7.5 11C9.43 11 11 9.43 11 7.5S9.43 4 7.5 4 4 5.57 4 7.5 5.57 11 7.5 11zm0-5C8.33 6 9 6.67 9 7.5S8.33 9 7.5 9 6 8.33 6 7.5 6.67 6 7.5 6zM4.81 20.19L20.19 4.81 18.78 3.4 3.4 18.78l1.41 1.41zM16.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm0 5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
    },
    {
        key: "fire",
        labelKey: "Fire",
        path:
            "M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z",
    },
    {
        key: "heart",
        labelKey: "Heart",
        path:
            "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z",
    },
    {
        key: "flash",
        labelKey: "Flash",
        path: "M7 2v11h3v9l7-12h-4l4-8z",
    },
    {
        key: "diamond",
        labelKey: "Diamond",
        path:
            "M19 3H5L2 9l10 12L22 9l-3-6zM9.62 8l1.5-3h1.76l1.5 3H9.62zM11 10v6.68L5.44 10H11zm2 0h5.56L13 16.68V10zm6.26-2h-2.65l-1.5-3h2.65l1.5 3zM6.24 5h2.65l-1.5 3H4.74l1.5-3z",
    },
];
